import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { tagopsService } from '../services/tagopsService';
import { useAuth } from './AuthContext';

interface ServiceConfig {
  [key: string]: any;
}

export interface Account {
  tenantId: string;
  type: string;
  status: string;
  account_name: string;
  role_name: string;
  externalId: string;
  regions?: string | string[];
  allowed_regions?: string | string[];
  allowerd_regions?: string;
  cloudformation_arn?: string;
  onboarding_region?: string;
  accountId: string;
  permissions?: boolean;
  [key: string]: any;
}

interface AccountsData {
  accounts: Account[];
}

interface ConfigContextType {
  serviceConfig: ServiceConfig | null;
  accountsData: AccountsData | null;
  loading: {
    serviceConfig: boolean;
    accounts: boolean;
  };
  error: {
    serviceConfig: string | null;
    accounts: string | null;
  };
  lastFetched: {
    serviceConfig: Date | null;
    accounts: Date | null;
  };
  refreshServiceConfig: () => Promise<void>;
  refreshAccounts: () => Promise<void>;
  isStale: {
    serviceConfig: boolean;
    accounts: boolean;
  };
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

// Cache durations
const SERVICE_CONFIG_CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
const ACCOUNTS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Cache keys
const SERVICE_CONFIG_CACHE_KEY = 'tagops_service_config_cache';
const ACCOUNTS_CACHE_KEY = 'tagops_accounts_cache';
const ACTIVITY_CACHE_KEY = 'tagops_last_activity';

interface CachedData<T> {
  data: T;
  timestamp: number;
}

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

interface ConfigProviderProps {
  children: React.ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [serviceConfig, setServiceConfig] = useState<ServiceConfig | null>(null);
  const [accountsData, setAccountsData] = useState<AccountsData | null>(null);
  const [loading, setLoading] = useState({
    serviceConfig: false,
    accounts: false
  });
  const [error, setError] = useState({
    serviceConfig: null as string | null,
    accounts: null as string | null
  });
  const [lastFetched, setLastFetched] = useState({
    serviceConfig: null as Date | null,
    accounts: null as Date | null
  });

  // Check if data is stale
  const isStale = {
    serviceConfig: lastFetched.serviceConfig ? 
      (Date.now() - lastFetched.serviceConfig.getTime()) > SERVICE_CONFIG_CACHE_DURATION : true,
    accounts: lastFetched.accounts ? 
      (Date.now() - lastFetched.accounts.getTime()) > ACCOUNTS_CACHE_DURATION : true
  };

  // Load data from cache on mount
  useEffect(() => {
    const loadServiceConfigFromCache = () => {
      try {
        const cached = localStorage.getItem(SERVICE_CONFIG_CACHE_KEY);
        if (cached) {
          const { data, timestamp }: CachedData<ServiceConfig> = JSON.parse(cached);
          const cacheAge = Date.now() - timestamp;
          
          if (cacheAge < SERVICE_CONFIG_CACHE_DURATION) {
            // Cache is still valid
            setServiceConfig(data);
            setLastFetched(prev => ({ ...prev, serviceConfig: new Date(timestamp) }));
          } else {
            // Cache is stale, remove it and clear state
            localStorage.removeItem(SERVICE_CONFIG_CACHE_KEY);
            setServiceConfig(null);
            setLastFetched(prev => ({ ...prev, serviceConfig: null }));
          }
        } else {
          // No cache, clear state
          setServiceConfig(null);
          setLastFetched(prev => ({ ...prev, serviceConfig: null }));
        }
      } catch (err) {
        // Invalid cache data, remove it and clear state
        localStorage.removeItem(SERVICE_CONFIG_CACHE_KEY);
        setServiceConfig(null);
        setLastFetched(prev => ({ ...prev, serviceConfig: null }));
      }
    };

    const loadAccountsFromCache = () => {
      try {
        const cached = localStorage.getItem(ACCOUNTS_CACHE_KEY);
        if (cached) {
          const { data, timestamp }: CachedData<AccountsData> = JSON.parse(cached);
          const cacheAge = Date.now() - timestamp;
          
          if (cacheAge < ACCOUNTS_CACHE_DURATION) {
            // Cache is still valid
            setAccountsData(data);
            setLastFetched(prev => ({ ...prev, accounts: new Date(timestamp) }));
          } else {
            // Cache is stale, remove it and clear state
            localStorage.removeItem(ACCOUNTS_CACHE_KEY);
            setAccountsData(null);
            setLastFetched(prev => ({ ...prev, accounts: null }));
          }
        } else {
          // No cache, clear state
          setAccountsData(null);
          setLastFetched(prev => ({ ...prev, accounts: null }));
        }
      } catch (err) {
        // Invalid cache data, remove it and clear state
        localStorage.removeItem(ACCOUNTS_CACHE_KEY);
        setAccountsData(null);
        setLastFetched(prev => ({ ...prev, accounts: null }));
      }
    };

    loadServiceConfigFromCache();
    loadAccountsFromCache();
  }, []);

  // Track user activity for smart refresh
  useEffect(() => {
    const updateActivity = () => {
      localStorage.setItem(ACTIVITY_CACHE_KEY, Date.now().toString());
    };

    // Track various user activities
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });

    // Initial activity timestamp
    updateActivity();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, []);

  // Save data to cache
  const saveServiceConfigToCache = useCallback((data: ServiceConfig) => {
    try {
      const cacheData: CachedData<ServiceConfig> = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(SERVICE_CONFIG_CACHE_KEY, JSON.stringify(cacheData));
    } catch (err) {
      // localStorage might be full or unavailable, ignore
    }
  }, []);

  const saveAccountsToCache = useCallback((data: AccountsData) => {
    try {
      const cacheData: CachedData<AccountsData> = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(ACCOUNTS_CACHE_KEY, JSON.stringify(cacheData));
    } catch (err) {
      // localStorage might be full or unavailable, ignore
    }
  }, []);

  // Check if user is active
  const isUserActive = useCallback(() => {
    const lastActivity = localStorage.getItem(ACTIVITY_CACHE_KEY);
    const now = Date.now();
    const activityThreshold = 10 * 60 * 1000; // 10 minutes
    const isTabVisible = !document.hidden;
    
    return isTabVisible && (!lastActivity || (now - parseInt(lastActivity)) < activityThreshold);
  }, []);

  // Fetch service config data from API
  const fetchServiceConfigData = useCallback(async (): Promise<ServiceConfig> => {
    // Check if we have authentication tokens before making API calls
    const idToken = localStorage.getItem('id_token');
    const accessToken = localStorage.getItem('access_token');
    
    if (!idToken && !accessToken) {
      throw new Error('No authentication tokens found');
    }

    const response = await tagopsService.getServiceConfig();
    return response;
  }, []);

  // Fetch accounts data from API
  const fetchAccountsData = useCallback(async (): Promise<AccountsData> => {
    // Check if we have authentication tokens before making API calls
    const idToken = localStorage.getItem('id_token');
    const accessToken = localStorage.getItem('access_token');
    
    if (!idToken && !accessToken) {
      throw new Error('No authentication tokens found');
    }

    const response = await tagopsService.getAccounts();
    return response;
  }, []);

  // Refresh service config data
  const refreshServiceConfig = useCallback(async (isBackgroundRefresh = false) => {
    if (!isBackgroundRefresh) {
      setLoading(prev => ({ ...prev, serviceConfig: true }));
    }
    setError(prev => ({ ...prev, serviceConfig: null }));
    
    try {
      const data = await fetchServiceConfigData();
      setServiceConfig(data);
      setLastFetched(prev => ({ ...prev, serviceConfig: new Date() }));
      saveServiceConfigToCache(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch service config';
      
      // Check if it's an authentication error
      if (errorMessage.includes('401') || errorMessage.includes('403') || errorMessage.includes('Unauthorized')) {
        // Session expired, clear cache and don't show error for background refresh
        if (!isBackgroundRefresh) {
          setError(prev => ({ ...prev, serviceConfig: 'Session expired. Please refresh the page.' }));
        }
        localStorage.removeItem(SERVICE_CONFIG_CACHE_KEY);
        setServiceConfig(null);
        setLastFetched(prev => ({ ...prev, serviceConfig: null }));
      } else {
        setError(prev => ({ ...prev, serviceConfig: errorMessage }));
      }
      
      if (!isBackgroundRefresh) {
        throw err;
      }
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(prev => ({ ...prev, serviceConfig: false }));
      }
    }
  }, [fetchServiceConfigData, saveServiceConfigToCache]);

  // Refresh accounts data
  const refreshAccounts = useCallback(async (isBackgroundRefresh = false) => {
    if (!isBackgroundRefresh) {
      setLoading(prev => ({ ...prev, accounts: true }));
    }
    setError(prev => ({ ...prev, accounts: null }));
    
    try {
      const data = await fetchAccountsData();
      setAccountsData(data);
      setLastFetched(prev => ({ ...prev, accounts: new Date() }));
      saveAccountsToCache(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch accounts data';
      
      // Check if it's an authentication error
      if (errorMessage.includes('401') || errorMessage.includes('403') || errorMessage.includes('Unauthorized')) {
        // Session expired, clear cache and don't show error for background refresh
        if (!isBackgroundRefresh) {
          setError(prev => ({ ...prev, accounts: 'Session expired. Please refresh the page.' }));
        }
        localStorage.removeItem(ACCOUNTS_CACHE_KEY);
        setAccountsData(null);
        setLastFetched(prev => ({ ...prev, accounts: null }));
      } else {
        setError(prev => ({ ...prev, accounts: errorMessage }));
      }
      
      if (!isBackgroundRefresh) {
        throw err;
      }
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(prev => ({ ...prev, accounts: false }));
      }
    }
  }, [fetchAccountsData, saveAccountsToCache]);

  // Initial fetch if no valid cache, but only when user is authenticated
  useEffect(() => {
    if (user && !serviceConfig && !loading.serviceConfig && !error.serviceConfig) {
      // No service config data, not loading, and no error - fetch initial data
      refreshServiceConfig().catch(() => {
        // Error handling is done in refreshServiceConfig
      });
    }
  }, [user, serviceConfig, loading.serviceConfig, error.serviceConfig, refreshServiceConfig]);

  useEffect(() => {
    if (user && !accountsData && !loading.accounts && !error.accounts) {
      // No accounts data, not loading, and no error - fetch initial data
      refreshAccounts().catch(() => {
        // Error handling is done in refreshAccounts
      });
    }
  }, [user, accountsData, loading.accounts, error.accounts, refreshAccounts]);

  // Auto-refresh if data is stale and we have cached data
  useEffect(() => {
    // Service config auto-refresh
    if (serviceConfig && isStale.serviceConfig && !loading.serviceConfig && isUserActive()) {
      refreshServiceConfig(true).catch(() => {
        // Ignore errors for background refresh
      });
    }
  }, [serviceConfig, isStale.serviceConfig, loading.serviceConfig, isUserActive, refreshServiceConfig]);

  useEffect(() => {
    // Accounts auto-refresh
    if (accountsData && isStale.accounts && !loading.accounts && isUserActive()) {
      refreshAccounts(true).catch(() => {
        // Ignore errors for background refresh
      });
    }
  }, [accountsData, isStale.accounts, loading.accounts, isUserActive, refreshAccounts]);

  const value: ConfigContextType = {
    serviceConfig,
    accountsData,
    loading,
    error,
    lastFetched,
    refreshServiceConfig,
    refreshAccounts,
    isStale
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};